import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { subscriptionApi } from '../services/api';
import { Subscription, SubscriptionNotification } from '../types';

interface SubscriptionDisplayProps {
  showNotifications?: boolean;
}

const SubscriptionDisplay: React.FC<SubscriptionDisplayProps> = ({ showNotifications = true }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [notifications, setNotifications] = useState<SubscriptionNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const [subscriptionResponse, notificationsResponse] = await Promise.all([
        subscriptionApi.getUserSubscription(),
        subscriptionApi.getNotifications(),
      ]);
      
      setSubscription(subscriptionResponse.data || null);
      setNotifications(notificationsResponse.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkNotificationRead = async (notificationId: number) => {
    try {
      await subscriptionApi.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
    } catch (err: any) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expiring_soon':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'expiring_soon':
        return <WarningIcon color="warning" />;
      case 'expired':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const unreadNotifications = notifications.filter(notif => !notif.is_read);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!subscription) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No subscription information available.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Subscription Status Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              Subscription Status
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {getStatusIcon(subscription.status)}
              <Chip
                label={subscription.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(subscription.status) as any}
                size="small"
              />
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Subscription Date:
              </Typography>
              <Typography variant="body2">
                {subscription.subscription_date ? formatDate(subscription.subscription_date) : 'N/A'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Renewal Date:
              </Typography>
              <Typography variant="body2">
                {subscription.renew_date ? formatDate(subscription.renew_date) : 'N/A'}
              </Typography>
            </Box>
            {subscription.days_remaining !== null && subscription.days_remaining !== undefined && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Days Remaining:
                </Typography>
                <Typography 
                  variant="body2" 
                  color={subscription.days_remaining <= 20 ? 'warning.main' : 'text.primary'}
                  fontWeight={subscription.days_remaining <= 20 ? 'bold' : 'normal'}
                >
                  {subscription.days_remaining} days
                </Typography>
              </Box>
            )}
          </Box>

          {/* Expiry Warning */}
          {subscription.status === 'expiring_soon' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your subscription will expire in {subscription.days_remaining} days. 
              Please contact the administrator to renew your subscription.
            </Alert>
          )}

          {subscription.status === 'expired' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Your subscription has expired. Please contact the administrator to renew your subscription.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      {showNotifications && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" component="h2">
                Notifications
              </Typography>
              {unreadNotifications.length > 0 && (
                <Badge badgeContent={unreadNotifications.length} color="error">
                  <Button
                    variant="outlined"
                    startIcon={<NotificationsIcon />}
                    onClick={() => setShowNotificationDialog(true)}
                    size="small"
                  >
                    View Notifications
                  </Button>
                </Badge>
              )}
            </Box>

            {notifications.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No notifications available.
              </Typography>
            ) : (
              <List dense>
                {notifications.slice(0, 3).map((notification) => (
                  <ListItem key={notification.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {notification.type === 'expiry_warning' && <WarningIcon color="warning" />}
                      {notification.type === 'expired' && <ErrorIcon color="error" />}
                      {notification.type === 'renewal_reminder' && <InfoIcon color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.message}
                      secondary={new Date(notification.created_at).toLocaleDateString()}
                      sx={{ 
                        opacity: notification.is_read ? 0.7 : 1,
                        fontWeight: notification.is_read ? 'normal' : 'bold'
                      }}
                    />
                    {!notification.is_read && (
                      <IconButton
                        size="small"
                        onClick={() => handleMarkNotificationRead(notification.id)}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notifications Dialog */}
      <Dialog 
        open={showNotificationDialog} 
        onClose={() => setShowNotificationDialog(false)}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            All Notifications
            <IconButton onClick={() => setShowNotificationDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {notifications.map((notification) => (
              <ListItem key={notification.id} divider>
                <ListItemIcon>
                  {notification.type === 'expiry_warning' && <WarningIcon color="warning" />}
                  {notification.type === 'expired' && <ErrorIcon color="error" />}
                  {notification.type === 'renewal_reminder' && <InfoIcon color="info" />}
                </ListItemIcon>
                <ListItemText
                  primary={notification.message}
                  secondary={new Date(notification.created_at).toLocaleDateString()}
                  sx={{ 
                    opacity: notification.is_read ? 0.7 : 1,
                    fontWeight: notification.is_read ? 'normal' : 'bold'
                  }}
                />
                {!notification.is_read && (
                  <IconButton
                    size="small"
                    onClick={() => handleMarkNotificationRead(notification.id)}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNotificationDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionDisplay;
